import React, { useState, useEffect } from 'react';
import {
  FileBarChart,
  Plus,
  Settings,
  Download,
  Calendar,
  Trash2,
  Copy,
  Clock,
  Mail,
  Loader2,
  BarChart3,
  PieChart,
  TrendingUp,
  Table,
  Grid,
  Eye,
  Save,
  ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart
} from 'recharts';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const widgetIcons = {
  metric_card: TrendingUp,
  line_chart: TrendingUp,
  bar_chart: BarChart3,
  pie_chart: PieChart,
  table: Table,
  heatmap: Grid
};

const ReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [widgetTypes, setWidgetTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [newReport, setNewReport] = useState({
    name: '',
    description: '',
    date_range: 'last_30_days',
    calendar_ids: [],
    platforms: [],
    widgets: [],
    layout: 'grid'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [reportsRes, templatesRes, widgetsRes] = await Promise.all([
        api.get('/reports/'),
        api.get('/reports/templates'),
        api.get('/reports/widget-types')
      ]);
      setReports(reportsRes.data);
      setTemplates(templatesRes.data);
      setWidgetTypes(widgetsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFromTemplate = async (templateId) => {
    try {
      const response = await api.post(`/reports/from-template/${templateId}?name=${encodeURIComponent(`${templateId} Report`)}`);
      toast.success('Report created from template!');
      loadData();
      viewReport(response.data.report_id);
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create report');
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.name) {
      toast.error('Please enter a report name');
      return;
    }

    try {
      const response = await api.post('/reports/', newReport);
      toast.success('Report created!');
      setShowCreateDialog(false);
      setNewReport({
        name: '',
        description: '',
        date_range: 'last_30_days',
        calendar_ids: [],
        platforms: [],
        widgets: [],
        layout: 'grid'
      });
      loadData();
      viewReport(response.data.report_id);
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create report');
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm('Are you sure you want to delete this report?')) return;

    try {
      await api.delete(`/reports/${reportId}`);
      toast.success('Report deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete report');
    }
  };

  const handleDuplicateReport = async (reportId) => {
    try {
      await api.post(`/reports/${reportId}/duplicate`);
      toast.success('Report duplicated!');
      loadData();
    } catch (error) {
      toast.error('Failed to duplicate report');
    }
  };

  const viewReport = async (reportId) => {
    try {
      const response = await api.get(`/reports/${reportId}`);
      setSelectedReport(response.data);
      setReportData(response.data.data);
      setShowViewDialog(true);
    } catch (error) {
      toast.error('Failed to load report');
    }
  };

  const handleExport = async (reportId, format) => {
    try {
      const response = await api.get(`/reports/${reportId}/export/${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toString() || '0';
  };

  const renderWidget = (widget, data) => {
    const widgetData = data?.[widget.data_source] || {};

    switch (widget.type) {
      case 'metric_card':
        return (
          <Card className="h-full">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">{widget.title}</p>
              <p className="text-3xl font-bold mt-2">
                {formatNumber(widgetData?.value || 0)}
              </p>
              {widgetData?.change !== undefined && (
                <p className={`text-sm mt-1 ${widgetData.change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {widgetData.change >= 0 ? '+' : ''}{widgetData.change}% from last period
                </p>
              )}
            </CardContent>
          </Card>
        );

      case 'line_chart':
        return (
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{widget.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={Array.isArray(widgetData) ? widgetData : []}>
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="reach" stroke="#10b981" fill="#10b98133" />
                  <Area type="monotone" dataKey="engagement" stroke="#3b82f6" fill="#3b82f633" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );

      case 'bar_chart':
        return (
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{widget.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={Array.isArray(widgetData) ? widgetData : []}>
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="reach" fill="#10b981" />
                  <Bar dataKey="engagement" fill="#3b82f6" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );

      case 'pie_chart':
        return (
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{widget.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={200}>
                <RechartsPie>
                  <Pie
                    data={Array.isArray(widgetData) ? widgetData : []}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    dataKey="reach"
                    nameKey="name"
                    label={({ name }) => name}
                  >
                    {(Array.isArray(widgetData) ? widgetData : []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        );

      case 'table':
        return (
          <Card className="h-full">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{widget.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Content</th>
                      <th className="text-left py-2 px-2">Platform</th>
                      <th className="text-right py-2 px-2">Engagement</th>
                      <th className="text-right py-2 px-2">Reach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(widgetData) ? widgetData.slice(0, 5) : []).map((row, i) => (
                      <tr key={i} className="border-b">
                        <td className="py-2 px-2 truncate max-w-[150px]">{row.content}</td>
                        <td className="py-2 px-2">
                          <Badge variant="outline" className="text-xs">
                            {row.platforms?.[0]}
                          </Badge>
                        </td>
                        <td className="py-2 px-2 text-right">{formatNumber(row.engagement)}</td>
                        <td className="py-2 px-2 text-right">{formatNumber(row.reach)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        );

      default:
        return (
          <Card className="h-full">
            <CardContent className="p-4 flex items-center justify-center h-full">
              <p className="text-slate-400">{widget.title}</p>
            </CardContent>
          </Card>
        );
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Custom Reports</h1>
            <p className="text-slate-500 mt-1">Build and export custom analytics reports</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-report-btn">
            <Plus className="w-4 h-4" />
            Create Report
          </Button>
        </div>

        <Tabs defaultValue="reports" className="space-y-6">
          <TabsList>
            <TabsTrigger value="reports">My Reports</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>

          {/* My Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            {reports.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileBarChart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="font-medium text-slate-700 mb-1">No reports created yet</h3>
                  <p className="text-sm text-slate-500 mb-4">Create custom reports to track your performance</p>
                  <Button onClick={() => setShowCreateDialog(true)} className="gap-2">
                    <Plus className="w-4 h-4" />
                    Create Your First Report
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {reports.map(report => (
                  <Card key={report.report_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold">{report.name}</h3>
                          <p className="text-xs text-slate-500 mt-1">
                            {report.description || `${report.widgets?.length || 0} widgets`}
                          </p>
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {report.date_range?.replace(/_/g, ' ')}
                        </Badge>
                      </div>

                      <div className="flex gap-1 mb-3">
                        {report.widgets?.slice(0, 4).map((w, i) => {
                          const Icon = widgetIcons[w.type] || BarChart3;
                          return (
                            <div key={i} className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                              <Icon className="w-4 h-4 text-slate-500" />
                            </div>
                          );
                        })}
                        {report.widgets?.length > 4 && (
                          <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-xs text-slate-500">
                            +{report.widgets.length - 4}
                          </div>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => viewReport(report.report_id)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleDuplicateReport(report.report_id)}>
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleExport(report.report_id, 'csv')}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => handleDeleteReport(report.report_id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => viewReport(report.report_id)}>
                          View <ChevronRight className="w-3 h-3 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map(template => (
                <Card key={template.template_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-semibold">{template.name}</h3>
                        <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                      </div>
                      <Badge variant="outline">{template.widgets?.length} widgets</Badge>
                    </div>

                    <div className="flex gap-1 mb-4">
                      {template.widgets?.slice(0, 6).map((w, i) => {
                        const Icon = widgetIcons[w.type] || BarChart3;
                        return (
                          <div key={i} className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center" title={w.title}>
                            <Icon className="w-4 h-4 text-slate-500" />
                          </div>
                        );
                      })}
                    </div>

                    <Button 
                      className="w-full"
                      onClick={() => handleCreateFromTemplate(template.template_id)}
                    >
                      Use This Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Create Report Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Custom Report</DialogTitle>
              <DialogDescription>
                Configure your report settings
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Report Name *</Label>
                <Input
                  value={newReport.name}
                  onChange={(e) => setNewReport({ ...newReport, name: e.target.value })}
                  placeholder="e.g., Monthly Performance"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newReport.description}
                  onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                  placeholder="Brief description of this report"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Date Range</Label>
                <Select
                  value={newReport.date_range}
                  onValueChange={(v) => setNewReport({ ...newReport, date_range: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="last_7_days">Last 7 Days</SelectItem>
                    <SelectItem value="last_30_days">Last 30 Days</SelectItem>
                    <SelectItem value="last_90_days">Last 90 Days</SelectItem>
                    <SelectItem value="custom">Custom Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Select Widgets</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {widgetTypes.map(wt => {
                    const Icon = widgetIcons[wt.type] || BarChart3;
                    const isSelected = newReport.widgets.some(w => w.type === wt.type);
                    return (
                      <button
                        key={wt.type}
                        onClick={() => {
                          if (isSelected) {
                            setNewReport({
                              ...newReport,
                              widgets: newReport.widgets.filter(w => w.type !== wt.type)
                            });
                          } else {
                            setNewReport({
                              ...newReport,
                              widgets: [...newReport.widgets, {
                                widget_id: `w_${Date.now()}`,
                                type: wt.type,
                                title: wt.name,
                                data_source: wt.data_sources[0],
                                position: { x: 0, y: 0, w: 4, h: 2 },
                                config: {}
                              }]
                            });
                          }
                        }}
                        className={`p-3 rounded-lg border text-center transition-all ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? 'text-emerald-600' : 'text-slate-400'}`} />
                        <p className="text-xs">{wt.name}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateReport}>
                  Create Report
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* View Report Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle>{selectedReport?.name}</DialogTitle>
                  <DialogDescription>
                    {selectedReport?.description || `${selectedReport?.date_range?.replace(/_/g, ' ')}`}
                  </DialogDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleExport(selectedReport?.report_id, 'csv')}>
                    <Download className="w-4 h-4 mr-1" />
                    CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleExport(selectedReport?.report_id, 'json')}>
                    <Download className="w-4 h-4 mr-1" />
                    JSON
                  </Button>
                </div>
              </div>
            </DialogHeader>

            {selectedReport && reportData && (
              <div className="grid grid-cols-12 gap-4 mt-4">
                {selectedReport.widgets?.map((widget, i) => (
                  <div 
                    key={i} 
                    className={`col-span-${Math.min(widget.position?.w || 4, 12)}`}
                    style={{ gridColumn: `span ${Math.min(widget.position?.w || 4, 12)}` }}
                  >
                    {renderWidget(widget, reportData)}
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ReportsPage;
